import React, { useState } from 'react';
import MainLayout from '../../../components/layout/MainLayout';
import { InputField } from '../../../components/controls/InputField'
import CheckboxControl from '../../../components/controls/CheckboxControl';
import SelectListControl from '../../../components/controls/SelectListControl';
import DatePickerControl from '../../../components/controls/DatePickerControl';
import ColorPickerControl from '../../../components/controls/ColorPickerControl';
import RadioButtonControl from '../../../components/controls/RadioButtonControl';
import AutoCompleteControl from '../../../components/controls/AutoCompleteControl';
import MultiSelectListControl from '../../../components/controls/MultiSelectListControl';
import { DialogControl } from '../../../components/controls/DialogControl';
import ButtonControl from '../../../components/controls/ButtonControl';
import { useAppUserManager } from '../../../hooks/useManagers';
import ItemManager from '../../../managers/ItemManager';

// Shared option list used by the Radio/AutoComplete/MultiSelect examples below.
const testTextValuePairs = [
  { Value: 'test1', Text: 'Test One' },
  { Value: 'test2', Text: 'Test Two' },
  { Value: 'test3', Text: 'Test Three' },
];

const ControlTest = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const authMgr = useAppUserManager();
  const [queryJson, setQueryJson] = useState("");
  const onChange = (value, fieldName) => {
    console.log(`${fieldName}: `, value);
  }

  // RadioButtonControl, AutoCompleteControl, and MultiSelectListControl are
  // normally rendered directly by FormControl.js (not as InputField
  // children) because their onChange callback shape is (value, field)
  // rather than a DOM event - InputField's child-cloning wraps onChange
  // assuming a DOM event, so composing them the same way as
  // SelectListControl/ColorPickerControl above would silently break their
  // onChange. They're wired up standalone here to match real usage.
  const [radioValue, setRadioValue] = useState('test1');
  const [autoCompleteValue, setAutoCompleteValue] = useState('Test One');
  const [multiSelectValues, setMultiSelectValues] = useState([]);

  // The Information/Description textareas and the two date pickers below
  // are controlled components (see TextAreaControl/DatePickerControl), so -
  // same as every real usage elsewhere in the app - they need the parent to
  // actually track value/onChange or typing into them will appear to do
  // nothing (every keystroke's onChange fires, but with no state update the
  // controlled value snaps right back to empty on the next render).
  const [infoValue, setInfoValue] = useState("");
  const [dialogDescriptionValue, setDialogDescriptionValue] = useState("");
  const [datePickerValue, setDatePickerValue] = useState("");
  const [dateTimePickerValue, setDateTimePickerValue] = useState("");
  const [checkboxValue, setCheckboxValue] = useState(false);
  const [singleSelectValue, setSingleSelectValue] = useState("UNK");

  return (<>
    <DialogControl openDialog={openDialog} title="Test Dialog" onCancel={async () => { setOpenDialog(false) }} onOk={async () => { setOpenDialog(false) }} subTitle={'Input an item'}>
      <InputField title="Name" groupId={'DIALOG'} maxLength={100} fieldName="Name" />
      <InputField title="Description" groupId={'DIALOG'} type='textarea' maxLength={100} fieldName="Description" value={dialogDescriptionValue} onChange={setDialogDescriptionValue} />
    </DialogControl>
    <MainLayout>
      <div className="screen-wrapper">
        <div className="screen-header">
          <h2>Control Test</h2>
          <h4>This screen is used to test the controls within the application.</h4>
        </div>
        <div className='control-box-wrapper'>
          <div className='control-box box-two-column'>
            <InputField title="Email Address" validateOnBlur={true} groupId={'TEST'} info={'Please enter your email address. This is a place where your email address is entered... a good place to do this.'} placeholder={'your.name@youremail.com'} maxLength={400} subTitle={"this is an email example"} fieldName="Email" validationName="test_Email" />
            <InputField title="Information" maxLength={100} groupId={'TEST'} subTitle={"this is an text area example"} type="textarea" fieldName="info" validationName="textBox" value={infoValue} onChange={setInfoValue} />
            <InputField title="Checkbox Test" maxLength={100} groupId={'TEST'} subTitle={"This is check box example"} fieldName="checkboxTest" value={checkboxValue} onChange={setCheckboxValue} >
              <CheckboxControl />
            </InputField>
            <InputField title="Single Select" maxLength={100} value={singleSelectValue} onChange={setSingleSelectValue} groupId={'TEST'} subTitle={"This is a single select example"} fieldName="singleSelect" >
              <SelectListControl textValuePairs={
                [{ Value: 'test1', Text: 'Test One' }, { Value: 'test2', Text: 'Test Two' }]
              } />
            </InputField>
            <InputField title="Date Picker" maxLength={100} value={datePickerValue} onChange={setDatePickerValue} groupId={'USER'} subTitle={"This is a date picker example"} fieldName="datePicker" >
              <DatePickerControl includeTime={false} />
            </InputField>
            <InputField title="Date Time Picker" maxLength={100} value={dateTimePickerValue} onChange={setDateTimePickerValue} groupId={'USER'} subTitle={"This is a date time picker example"} fieldName="dateTimePicker" >
              <DatePickerControl includeTime={true} />
            </InputField>
          </div>
          <div className='control-box box-two-column'>
            <InputField title="test" validateOnBlur={true} groupId={'USER'} maxLength={100} subTitle={"this is an test"} fieldName="test" validationName="user_test" />
            <InputField title="color Picker" maxLength={100} onChange={onChange} groupId={'USER'} subTitle={"This is a color picker example"} fieldName="colorPicker" >
              <ColorPickerControl />
            </InputField>
            <br />
            <div>Radio Button Test</div>
            <RadioButtonControl
              field={{ FieldName: 'radioTest', DisplayName: 'Radio Button Test', IsRequired: true }}
              value={radioValue}
              textValuePairs={testTextValuePairs}
              onChange={(context) => setRadioValue(context.target.value)}
              onBlur={() => { }}
            />
            <br />
            <div>Auto Complete Test</div>
            <AutoCompleteControl
              field={{ FieldName: 'autoCompleteTest', DisplayName: 'Auto Complete Test', IsRequired: true }}
              value={autoCompleteValue}
              textValuePairs={testTextValuePairs}
              onChange={(obj) => setAutoCompleteValue(obj?.Text ?? "")}
              onBlur={() => { }}
            />
            <br />
            <div>Multi Select Test</div>
            <MultiSelectListControl
              field={{ FieldName: 'multiSelectTest', DisplayName: 'Multi Select Test', IsRequired: true }}
              values={multiSelectValues}
              textValuePairs={testTextValuePairs}
              onChange={(selectedList) => setMultiSelectValues(selectedList)}
              onBlur={() => { }}
            />
            <br />
            <ButtonControl onClick={() => { setOpenDialog(true) }}>Open Dialog</ButtonControl>
            <br />
            <ButtonControl to={'/content'} >Go To Content</ButtonControl>
            <InputField title="Query Builder" onChange={(v, f) => {
              try {
                if (v !== "")
                  setQueryJson(JSON.stringify(ItemManager.JSONObjFilter(v), null, "\t"));
              }
              catch (e) {
                setQueryJson("Invalid Query" + e);
              }
            }
            }></InputField>
            <pre style={{ cursor: "pointer" }} onClick={() => { navigator.clipboard.writeText(queryJson.replaceAll('"', `\\"`)); }}>{queryJson}</pre>
          </div>
          <div className='screen-footer'>
            <div className='btn-wrapper-left screen-footer__btn'><ButtonControl type={'okay'} onClick={() => { alert("Save Button") }}>Save</ButtonControl></div>
            <div className='btn-wrapper-right screen-footer__btn'><ButtonControl type={'cancel'} onClick={() => { alert("Cancel Button") }}>Cancel</ButtonControl></div>
          </div>
        </div>

      </div>
    </MainLayout>
  </>
  );
}

export default ControlTest;